import { IconButton } from '@creditornot/cb-button';
import { ArrowLeft, ArrowRight } from '@creditornot/cb-icons';
import { DateTime } from 'luxon';
import React, { useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import { OfflineTimeInsight } from 'apis/merchant-gateway/insights';
import { InsightEntity, InsightResponseWithPreviousInterval } from 'modules/insights-v2/types';

import OfflineTimeLayout from './OfflineTimeLayout';
import { OfflineInterval, OfflineMinutesPerHour } from './types';
import { HeatMap, HeatMapDatum } from '../../charts/Heatmap';
import { Legend } from '../../charts/types';

type OfflineTimePerHourProps = {
  insightEntityType: InsightEntity['type'];
  offlineTimeInsight: InsightResponseWithPreviousInterval<OfflineTimeInsight>;
  offlineInterval: OfflineInterval;
  legends: Record<OfflineMinutesPerHour, Legend>;
  setOfflineInterval: (offlineInterval: OfflineInterval) => void;
};

const ChartNavigation = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const dateRangeIterator = (startDate: string, endDate: string): Iterable<string> => {
  const start = DateTime.fromISO(startDate);
  const end = DateTime.fromISO(endDate);

  return {
    *[Symbol.iterator]() {
      let current = start;
      while (current <= end) {
        yield current.toISODate();
        current = current.plus({ days: 1 });
      }
    },
  };
};

const hourRangeIterator = (startHour: number, endHour: number): Iterable<string> => {
  return {
    *[Symbol.iterator]() {
      let current = startHour;
      while (current <= endHour) {
        yield current.toString();
        current++;
      }
    },
  };
};

export const OfflineTimePerHour = ({
  offlineTimeInsight,
  legends,
  insightEntityType,
  offlineInterval,
  setOfflineInterval,
}: OfflineTimePerHourProps) => {
  const chartData = useMemo((): HeatMapDatum<OfflineMinutesPerHour>[] => {
    const dataSplitByDay = offlineTimeInsight.selected.data?.data.offlineTime?.split ?? [];
    return dataSplitByDay
      .map((data): HeatMapDatum<OfflineMinutesPerHour>[] => {
        const { label: dayLabel, split } = data;
        const dataSplitByHour = split ?? [];
        return dataSplitByHour
          .map((data): (false | HeatMapDatum<OfflineMinutesPerHour>)[] => {
            const {
              label: hourLabel,
              total: { offlineMinutes, nonAliveMinutes },
            } = data;

            return [
              offlineMinutes > 0 && {
                x: dayLabel ?? '',
                y: hourLabel ?? '',
                value: offlineMinutes,
                key: OfflineMinutesPerHour.MANUALLY_PUT_OFFLINE,
              },
              nonAliveMinutes > 0 && {
                x: dayLabel ?? '',
                y: hourLabel ?? '',
                value: nonAliveMinutes,
                key: OfflineMinutesPerHour.OFFLINE_DUE_TO_ISSUE,
              },
            ];
          })
          .flat()
          .filter((v): v is HeatMapDatum<OfflineMinutesPerHour> => v !== false);
      })
      .flat();
  }, [offlineTimeInsight]);

  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(0);

  const handleResize = useCallback(
    (width: number) => {
      // Total width - Axis Widths - Padding / Block Width
      const newPageSize = Math.floor((width - 180) / 45);
      setPageSize(newPageSize);
    },
    [setPageSize],
  );

  const totalPages = useMemo(() => {
    if (!offlineTimeInsight.selected.data?.startDate || !offlineTimeInsight.selected.data?.endDate) {
      return 0;
    }
    const start = DateTime.fromISO(offlineTimeInsight.selected.data?.startDate);
    const end = DateTime.fromISO(offlineTimeInsight.selected.data?.endDate);
    const totalDays = end.diff(start, 'days').days;
    return Math.ceil(totalDays / pageSize);
  }, [offlineTimeInsight, pageSize]);

  const startDate = useMemo(() => {
    if (!offlineTimeInsight.selected.data?.startDate || !offlineTimeInsight.selected.data?.endDate) {
      return null;
    }
    const start = DateTime.fromISO(offlineTimeInsight.selected.data?.startDate);
    return start.plus({ days: currentPage * pageSize }).toISODate();
  }, [offlineTimeInsight, currentPage, pageSize]);

  const endDate = useMemo(() => {
    if (!startDate || !offlineTimeInsight.selected.data?.startDate || !offlineTimeInsight.selected.data?.endDate) {
      return null;
    }
    const start = DateTime.fromISO(startDate);
    const pageSizeEndDate = start.plus({ days: pageSize });
    const end = DateTime.fromISO(offlineTimeInsight.selected.data?.endDate);
    const finalEndDate = pageSizeEndDate < end ? pageSizeEndDate : end;
    return finalEndDate.toISODate();
  }, [offlineTimeInsight, startDate, pageSize]);

  const handlePreviousPage = useCallback(() => {
    setCurrentPage(currentPage => currentPage - 1);
  }, []);

  const handleNextPage = useCallback(() => {
    setCurrentPage(currentPage => currentPage + 1);
  }, []);

  if (!startDate || !endDate) {
    return null;
  }

  const translations = Object.fromEntries(Object.entries(legends).map(([key, { value }]) => [key, value])) as Record<
    OfflineMinutesPerHour,
    string
  >;

  return (
    <OfflineTimeLayout
      insightEntityType={insightEntityType}
      offlineInterval={offlineInterval}
      setOfflineInterval={setOfflineInterval}
      actions={
        <ChartNavigation>
          <IconButton variant="blue" size="small" onClick={handlePreviousPage} disabled={currentPage <= 0}>
            <ArrowLeft />
          </IconButton>
          <IconButton variant="blue" size="small" onClick={handleNextPage} disabled={currentPage >= totalPages - 1}>
            <ArrowRight />
          </IconButton>
        </ChartNavigation>
      }
    >
      <HeatMap
        onResize={handleResize}
        iteratorX={dateRangeIterator(startDate, endDate)}
        formatterX={dateString => DateTime.fromISO(dateString).toLocaleString({ day: 'numeric', month: 'numeric' })}
        iteratorY={hourRangeIterator(0, 23)}
        formatterY={hourString => `${hourString}:00`}
        legends={Object.entries(legends).map(([key, { color }]) => ({
          color,
          key,
        }))}
        formatterBlock={value => `${value}m`}
        data={chartData}
        translations={translations}
      ></HeatMap>
    </OfflineTimeLayout>
  );
};
